import { CheckCircle2, GitBranch, GitPullRequest, GitCommit, Shield } from "lucide-react";
import githubLogo from "@/assets/github-logo.png";
import { Footer } from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Link } from "react-router-dom";
import { WaitlistCTA } from "@/components/WaitlistCTA";

export default function GitHub() {

  return (
    <div className="min-h-screen bg-background">
      <ThemeToggle />
      
      {/* Header */}
      <section className="py-8 px-4 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                <GitBranch className="w-4 h-4" />
                <span className="text-sm font-medium">GitHub Integration</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Automated Code Fixes & PR Generation
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Healion connects directly to your GitHub repositories, automatically detecting issues and generating pull requests with high-quality fixes. Ship better code, faster.
              </p>
            </div>
            <div className="relative h-[300px] rounded-xl bg-background border border-border flex items-center justify-center p-12">
              <img src={githubLogo} alt="GitHub Logo" className="w-full h-full object-contain dark:invert" />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How GitHub Integration Works</h2>
            <p className="text-xl text-muted-foreground">Automated bug fixes from detection to deployment</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Connect Repository",
                description: "Link your GitHub repos and grant Healion access to monitor issues",
                icon: GitBranch,
              },
              {
                step: "2",
                title: "Analyze Codebase",
                description: "AI scans your code for bugs, security issues, and optimization opportunities",
                icon: Shield,
              },
              {
                step: "3",
                title: "Generate Fixes",
                description: "Healion creates production-ready code fixes following your team's conventions",
                icon: GitCommit,
              },
              {
                step: "4",
                title: "Create Pull Request",
                description: "Automatic PR with detailed descriptions, tests, and ready for review",
                icon: GitPullRequest,
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <div className="text-sm text-primary font-semibold mb-2">Step {item.step}</div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Healion for GitHub?</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Smart PR Generation",
                description: "Every PR includes comprehensive descriptions, test coverage, and follows your coding standards",
              },
              {
                title: "Security First",
                description: "Identifies and fixes security vulnerabilities before they reach production",
              },
              {
                title: "Seamless Workflow",
                description: "Integrates with your existing GitHub workflow, CI/CD, and code review process",
              },
            ].map((benefit, index) => (
              <div key={index} className="bg-background rounded-xl p-6 border border-border">
                <CheckCircle2 className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Transform Your GitHub Workflow</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join the waitlist to get early access to Healion's GitHub integration
          </p>
          <WaitlistCTA source="GitHub Page" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
